import { useQueryClient, useQuery, skipToken } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from './applicationQueries';

/**
 * Reads and writes a single field in the React Query application cache.
 *
 * Replaces the `const [x, set_x] = useRecoilState(someAtom)` pattern.
 * Uses useQuery with `select` so the component re-renders only when the
 * selected value changes.
 *
 * @param selector  - reads the field value from IApplicationData
 * @param updater   - returns a new IApplicationData with the field updated
 * @returns         [currentValue, setter]
 *
 * @example
 * const [firstName, set_firstName] = useApplicationField(
 *     (d) => d.information.borrower.firstName,
 *     (d, v) => ({ ...d, information: { ...d.information, borrower: { ...d.information.borrower, firstName: v } } })
 * );
 */
export function useApplicationField<T>(
    selector: (data: IApplicationData) => T,
    updater: (data: IApplicationData, value: T) => IApplicationData
): [T | undefined, (value: T) => void] {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    const { data: currentValue } = useQuery<IApplicationData, Error, T | undefined>({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: skipToken,
        select: selector,
    });

    const setValue = (value: T) => {
        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid ?? ''), (old) => {
            if (!old) return old;
            return updater(old, value);
        });
    };

    return [currentValue, setValue];
}
